import React from "react";
import { FixedSizeList as List } from "react-window";
import { Box, Flex, Icon, Spinner, Text } from "@chakra-ui/react";
import AutoSizer from "react-virtualized-auto-sizer";
import NoItemsDefaultNew from "./partials/NoItemsDefaultNew";
import { FaSortUp, FaSortDown } from "react-icons/fa6";
import useThemeColors from "../../../hooks/useThemeColors";
import {
  hoverItemColor,
  primary,
  // primaryAlt,
  textLightBlue,
  textSecondary,
  white,
} from "../../../utils/colors";
import useIsMobile from "../../../hooks/useIsMobile";

const TableContainerWindowed = ({
  loading,
  data,
  columns,
  noItemsTitle,
  noItemsDescription,
  noItemsButtonHandler,
  noItemsButtonLabel,
  noItemsButtonDisabled,
  onRowClick,
  sortField,
  sortOrder,
  handleSortChange,
  isBorderRadius,
  p,
}) => {
  const isMobile = useIsMobile();
  const { bgLightMode, newBorderMode } = useThemeColors();

  const rowHeight = 70; // Row height in pixels
  const headerHeight = 50; // Header height in pixels
  const totalHeight = headerHeight + data.length * rowHeight; // Corrected

  // console.log("pageSize", pageSize);
  // console.log("rowHeight", rowHeight);
  // console.log("totalHeight", totalHeight);

  const calculateGridTemplateColumns = (columns, totalWidth) => {
    const fixedWidths = columns
      .filter((col) => col.width)
      .reduce((acc, col) => acc + parseInt(col.width, 10), 0);

    const flexibleColumnsCount = columns.filter((col) => !col.width).length;
    const remainingWidth = totalWidth - fixedWidths;
    const dynamicWidth =
      flexibleColumnsCount > 0 ? remainingWidth / flexibleColumnsCount : 0;

    return columns
      .map((col) => (col.width ? `${col.width}px` : `${dynamicWidth}px`))
      .join(" ");
  };

  if (loading) {
    return (
      <Flex w="100%" justify="center" align="center" p="6rem 2rem">
        <Spinner size="lg" color={primary} />
      </Flex>
    );
  }

  if (!data || data.length === 0) {
    return (
      <NoItemsDefaultNew
        noItemsButtonDisabled={noItemsButtonDisabled}
        noItemsButtonHandler={noItemsButtonHandler}
        noItemsButtonLabel={noItemsButtonLabel}
        noItemsDescription={noItemsDescription}
        noItemsTitle={noItemsTitle}
      />
    );
  }

  const renderRow = ({ index, style, gridTemplateColumns }) => {
    const rowData = data[index];

    return (
      <Flex
        align="center"
        key={data[index]._id}
        style={{
          ...style, // Necessary for react-window's positioning
          // borderBottom: `1px solid ${newBorderMode}`,
          display: "grid",
          gridTemplateColumns,
          padding: "0", // Reset padding here
          // last
          borderBottom:
            index === data.length - 1 ? "none" : `1px solid ${newBorderMode}`,
        }}
        onClick={onRowClick ? () => onRowClick(data[index]._id) : undefined} // Row navigation
        _hover={{
          bg: isMobile ? white : hoverItemColor,
          cursor: onRowClick ? "pointer" : "default",
        }}
        _active={{
          bg: isMobile ? hoverItemColor : hoverItemColor,
        }}
      >
        {columns.map((column, colIndex) => (
          <Box
            pl={colIndex === 0 ? (isMobile ? "1.5rem" : "2rem") : p ? p : 0}
            pr={
              colIndex === columns.length - 1
                ? isMobile
                  ? "1.51rem"
                  : "2rem"
                : p
                ? p
                : 0
            }
            key={column.accessor}
            textAlign="center"
            // onClick={(e) => {
            //   e.stopPropagation(); // ✅ Allows row clicks while keeping inside clicks separate
            //   if (column.onClick) column.onClick(data[index]); // ✅ Allows specific column clicks
            // }}
            onClick={(e) => {
              if (column.preventRowClick) {
                e.stopPropagation(); // ✅ Prevent row click when clicking buttons
              } else {
                onRowClick && onRowClick(rowData._id); // ✅ Clickable columns trigger row navigation
              }
            }}
          >
            {column.Cell
              ? column.Cell({ row: { original: data[index] } })
              : data[index][column.accessor]}
          </Box>
        ))}
      </Flex>
    );
  };

  const renderSortIcon = (columnAccessor) => {
    if (sortField === columnAccessor) {
      return sortOrder === "asc" ? (
        <Icon
          aria-label="sorted ascending"
          as={FaSortUp}
          fontSize="0.875rem"
          color={textSecondary}
          position="relative"
          top="3px"
          ml={1}
        />
      ) : (
        <Icon
          aria-label="sorted descending"
          as={FaSortDown}
          fontSize="0.875rem"
          color={textSecondary}
          position="relative"
          top="-4px"
          ml={1}
        />
      );
    }
    return null;
  };

  return (
    <Box w="100%" overflowX="visible" minH={`${totalHeight}px`} zIndex="9">
      <AutoSizer>
        {({ width }) => {
          const gridTemplateColumns = calculateGridTemplateColumns(
            columns,
            width
          );

          return (
            <>
              <Flex
                h="50px"
                w="100%"
                align="center"
                bg={bgLightMode}
                display="grid"
                gridTemplateColumns={gridTemplateColumns}
              >
                {columns.map((column, colIndex) => (
                  <Flex
                    borderTopLeftRadius={
                      isBorderRadius ? (colIndex === 0 ? "1rem" : 0) : 0
                    }
                    borderTopRightRadius={
                      isBorderRadius
                        ? colIndex === columns.length - 1
                          ? "1rem"
                          : 0
                        : 0
                    }
                    key={column.accessor}
                    fontWeight="bold"
                    cursor="pointer"
                    textAlign="center"
                    onClick={() => handleSortChange(column.accessor)}
                    bg={bgLightMode}
                    h="50px"
                    w="100%"
                    justify={
                      colIndex === columns.length - 1
                        ? "flex-end"
                        : "flex-start"
                    }
                  >
                    <Flex
                      align="center"
                      h="100%"
                      pl={colIndex === 0 ? (isMobile ? "1.5rem" : "2rem") : 0}
                      pr={
                        colIndex === columns.length - 1
                          ? isMobile
                            ? "1.5rem"
                            : "2rem"
                          : 0
                      }
                    >
                      <Text
                        as="span"
                        fontSize="0.688rem"
                        fontWeight="600"
                        color={textLightBlue}
                        textTransform="uppercase"
                      >
                        {typeof column.Header === "function"
                          ? column.Header()
                          : column.Header}
                      </Text>
                      {renderSortIcon(column.accessor)}
                    </Flex>
                  </Flex>
                ))}
              </Flex>

              <List
                height={data.length * rowHeight}
                itemCount={data.length}
                itemSize={70}
                width={width}
                style={{ overflow: "visible" }}
              >
                {({ index, style }) =>
                  renderRow({ index, style, gridTemplateColumns })
                }
              </List>
            </>
          );
        }}
      </AutoSizer>
    </Box>
  );
};

export default TableContainerWindowed;
